import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, ShieldCheck, MapPin, Users, Award, Briefcase, Target, Cpu, Calendar } from "lucide-react";
import { companyInfo, whyChooseUs } from "../data";
import useMetadata from "../hooks/useMetadata";

const iconMap = {
  Briefcase: Briefcase,
  Target: Target,
  Cpu: Cpu,
  Calendar: Calendar,
  ShieldCheck: ShieldCheck,
};

export default function About() {
  useMetadata(
    "Om os",
    "Læs mere om Godske Byg & Anlæg, en ambitiøs enkeltmandsvirksomhed ejet af Marcel Godske-Hjembæk. Vi har stort fokus på kvalitet, holdbarhed og præcision."
  );

  return (
    <div className="bg-white text-brand-charcoal">
      {/* Presentation Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-center">
            {/* Left Column: Text */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl uppercase text-brand-charcoal">
                Kvalitet, præcision og pålidelighed i højsædet
              </h1>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Godske Byg og Anlæg er er ambitiøs og erfaren enkeltmandsvirksomhed med mig, Marcel Godske-Hjembæk, som indehaver. Jeg holder til i Mørkøv, og kører ud i 30 kilometers radius med mine kvalitetsrige og professionelle løsninger til både private, erhverv og boligforeninger. Jeg er specialist i alt det grå anlægsarbejde. Vi har specialiseret os 100% inden for jord-, grave- og anlægsarbejde. For at sikre den absolut højeste kvalitet og det mest fokuserede arbejde, udfører vi bevidst <strong>ikke</strong> murerarbejde, støttemure eller traditionelt tømrerarbejde. Vi gør det, vi er bedst til – og det gør vi til perfektion.
              </p>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Med Godske Byg og Anlæg kan du forvente en god dialog fra start til slut. Jeg prioriterer altid mine kunder gennem et projekt, så du kan være sikker på, at jeg overholder vores aftaler. Med andre ord godkender jeg at møde op til aftalt tid, og leverer et stærkt produkt til den indenfor aftalt tidspunkt.
              </p>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Af mine kunder får jeg ros for et veludført stykke arbejde, der står snorlige og med en flot finish. Gennem flere år i branchen har jeg opnået en fagkærlig tilgang til mine projekter, så du kan være sikker på at jeg ikke forlader din matrikel før både du og jeg selv er 100 procent tilfredse med det færdige resultat.
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-charcoal shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-600 font-medium">Stærkt lokalkendskab i og omkring Mørkøv</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-charcoal shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-600 font-medium">Moderne maskinpark med GPS-styring</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-charcoal shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-600 font-medium">Pålidelig og effektiv projektstyring</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-charcoal shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-600 font-medium">100% fokus på jord- og gravearbejde</span>
                </div>
              </div>
            </div>

            {/* Right Column: Image */}
            <div className="lg:col-span-5">
              <div className="relative rounded-none overflow-hidden border border-gray-200 bg-gray-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=600,fit=cover,quality=80,format=auto/assets/b6ab12dc-9087-41bb-8b7f-45b54510818a/gravemaskine-arbejde.jpg"
                  alt="Godske Byg & Anlæg - Gravemaskine i arbejde"
                  className="w-full h-full object-cover aspect-square"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-gray-50 py-12 sm:py-16 border-none text-brand-charcoal">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-charcoal sm:text-4xl mt-2 uppercase">
              Hvorfor vælge Godske Byg & Anlæg?
            </h2>
            <p className="mt-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              Vi adskiller os ved vores kompromisløse fokus på kvalitet, præcision og moderne teknologi. Vi udfører ikke murerarbejde eller støttemure, men er 100% specialiserede i jord- og gravearbejde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.slice(0, 4).map((item, index) => {
              const IconComponent = iconMap[item.icon];
              const isFirst = index === 0;
              return (
                <div
                  key={item.title}
                  className={`p-8 transition-all duration-300 rounded-none border-none shadow-none flex flex-col justify-between ${
                    isFirst
                      ? "bg-brand-charcoal text-white"
                      : "bg-brand-gray-light text-brand-charcoal"
                  }`}
                >
                  <div>
                    <div className={`mb-6 ${isFirst ? "text-white" : "text-brand-accent"}`}>
                      {IconComponent && <IconComponent className="h-8 w-8" />}
                    </div>
                    <h3
                      className={`text-lg font-medium tracking-tight mb-3 uppercase ${
                        isFirst ? "text-white" : "text-brand-charcoal"
                      }`}
                    >
                      {item.title}
                    </h3>
                    <p className={`text-sm leading-relaxed ${isFirst ? "text-gray-300" : "text-gray-600"}`}>
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 text-center bg-gray-50 border-t border-gray-100">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-charcoal sm:text-3xl uppercase">
            Lad os tage en uforpligtende snak
          </h2>
          <p className="mt-3 text-gray-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Har du spørgsmål til vores ydelser, eller ønsker du rådgivning om dit kommende anlægsprojekt? Vi sidder klar til at hjælpe dig videre.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-none bg-brand-accent px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-accent-hover"
            >
              Kontakt os her
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
